import { useEffect, useState, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { getSocket, getExistingSocket, disconnectSocket } from '../multiplayer/socket.ts';
import { useAuth } from './useAuth.ts';
import useSettings from './useSettings.ts';

export interface UseSocketReturn {
  socket: Socket | null;
  connected: boolean;
  error: string | null;
}

export function useSocket(): UseSocketReturn {
  const { isAuthenticated, getAccessTokenSilently } = useAuth();
  const [connected, setConnected] = useState(() => getExistingSocket()?.connected ?? false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(getExistingSocket());

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      socketRef.current = null;
      setConnected(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let socket: ReturnType<typeof getSocket> | null = null;

    const onConnect = () => { if (!cancelled) setConnected(true); };
    const onDisconnect = () => { if (!cancelled) setConnected(false); };
    const onConnectError = (err: Error) => { if (!cancelled) setError(err.message); };

    getAccessTokenSilently().then(token => {
      if (cancelled) return;

      // Read opt-out flags at connect time (not as effect deps — live changes
      // are pushed via PRESENCE_SET_STATUS, so they must not re-create the socket).
      const { appearOnline, acceptChallenges } = useSettings.getState();
      socket = getSocket(token, { appearOnline, acceptChallenges });
      socketRef.current = socket;

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onConnectError);

      if (socket.connected) {
        setConnected(true);
      }
    }).catch(err => {
      if (!cancelled) setError(err.message);
    });

    return () => {
      cancelled = true;
      if (socket) {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('connect_error', onConnectError);
      }
      // Do NOT disconnect — the singleton socket must survive navigation
      // between multiplayer screens. Disconnect only on logout (isAuthenticated → false).
    };
  }, [isAuthenticated]);

  return { socket: socketRef.current, connected, error };
}
